import React from 'react';
import { Plus, ArrowRight, ArrowLeft, Download, HelpCircle } from 'lucide-react';
import { UNITS } from '../../contexts/AppContext';
import { downloadIngredientTemplate } from '../../utils/csvTemplates';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import WhyBox from './WhyBox';

export default function WizardIngredients({ ingredients, ingForm, setIngForm, onAddIngredient, onNext, onBack }) {
  return (
    <div data-testid="wizard-ingredients">
      <div className="pizza-card p-6 mb-4">
        <h2 className="text-3xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>
          What Goes Into Your Food?
        </h2>
        <p className="text-sm text-[#2A2A2A]/60 mb-4">
          Don't worry about getting everything perfect. Start with your top 5-6 ingredients — the ones you buy the most of. You can always come back and add more.
        </p>

        <WhyBox title="Why Track Ingredient Costs?">
          <p>Every menu item you sell is made of ingredients that cost money. <strong>Food cost percentage</strong> = (ingredient cost / menu price) x 100. For a pizza shop, you want this under 30%.</p>
          <p>Example: If your cheese pizza uses $4 in ingredients and you sell it for $16, your food cost is 25%. That's good. If it's $6 in ingredients, that's 37.5% — and you're in trouble.</p>
          <p className="text-xs text-[#2A2A2A]/50 italic">Source: National Restaurant Association — target food cost for pizza: 25-32%</p>
        </WhyBox>

        <div className="bg-[#FFF8E7] rounded-lg p-4 mb-4 border border-[#2A2A2A]/10">
          <p className="text-sm font-semibold text-[#2A2A2A] mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>Add an Ingredient</p>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">What is it?</label>
              <input type="text" placeholder="e.g. Mozzarella" value={ingForm.name}
                onChange={e => setIngForm(f => ({ ...f, name: e.target.value }))}
                className="pizza-input w-full text-sm" data-testid="wizard-ing-name" />
            </div>
            <div>
              <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">Where do you buy it?</label>
              <input type="text" placeholder="e.g. Sysco" value={ingForm.vendor}
                onChange={e => setIngForm(f => ({ ...f, vendor: e.target.value }))}
                className="pizza-input w-full text-sm" data-testid="wizard-ing-vendor" />
            </div>
            <div>
              <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">How much do you pay? ($)</label>
              <input type="number" step="0.01" min="0" placeholder="e.g. 48.50" value={ingForm.purchaseCost}
                onChange={e => setIngForm(f => ({ ...f, purchaseCost: e.target.value }))}
                className="pizza-input w-full text-sm" data-testid="wizard-ing-cost" />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">How much comes in it?</label>
                <input type="number" step="0.01" min="0.01" placeholder="e.g. 10" value={ingForm.purchaseQuantity}
                  onChange={e => setIngForm(f => ({ ...f, purchaseQuantity: e.target.value }))}
                  className="pizza-input w-full text-sm" data-testid="wizard-ing-qty" />
              </div>
              <div className="w-24">
                <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">Unit</label>
                <Select value={ingForm.purchaseUnit} onValueChange={v => setIngForm(f => ({ ...f, purchaseUnit: v }))}>
                  <SelectTrigger className="border-2 border-[#2A2A2A] rounded-lg text-sm h-[42px]" data-testid="wizard-ing-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <button onClick={onAddIngredient} disabled={!ingForm.name.trim()}
            className="pizza-btn text-xs px-5 py-2 disabled:opacity-40" data-testid="wizard-add-ing-btn">
            <Plus className="w-3.5 h-3.5 inline mr-1" /> Add to List
          </button>
        </div>

        {ingredients.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Your Ingredients ({ingredients.length})
            </p>
            <div className="space-y-1">
              {ingredients.map(ing => (
                <div key={ing.id} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-[#2A2A2A]/10 text-sm">
                  <span className="font-medium text-[#2A2A2A]">{ing.name}</span>
                  <span className="text-[#E23D28] font-medium">${(ing.purchaseCost || 0).toFixed(2)} / {ing.purchaseQuantity} {ing.purchaseUnit}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-[#F0E6D2] pt-4">
          <p className="text-xs text-[#2A2A2A]/50 mb-2">Prefer a spreadsheet? Download our template, fill it in, and upload it on the Upload page.</p>
          <button onClick={downloadIngredientTemplate}
            className="pizza-btn-secondary text-xs px-4 py-1.5 flex items-center gap-1" data-testid="wizard-download-ing-template">
            <Download className="w-3.5 h-3.5" /> Download Ingredient Template
          </button>
        </div>
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="pizza-btn-secondary text-sm flex items-center gap-1" data-testid="wizard-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={onNext} className="pizza-btn text-sm flex items-center gap-1" data-testid="wizard-next-btn"
          disabled={ingredients.length === 0}>
          Next: Build a Recipe <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
